/**
 * This custom Rehype plugin is based on https://github.com/JS-DevTools/rehype-toc.
 */

import { Processor, Transformer } from 'unified';
import { Node, Parent } from 'unist';

const HEADINGS = ['h2', 'h3', 'h4'] as const;
type HeadingTagName = typeof HEADINGS[number];

interface HtmlElementNode extends Node {
  type: 'element';
  tagName: string;
  properties: {
    [prop: string]: string | undefined;
  };
  children?: Node[];
}

interface HeadingNode extends HtmlElementNode {
  tagName: HeadingTagName;
}

export interface TableOfContentsListNode {
  type: 'list';
  depth: number;
  children: TableOfContentsListItemNode[];
}

export interface TableOfContentsListItemNode {
  type: 'item';
  children: Array<TableOfContentsListNode | TableOfContentsTextNode>;
}

export interface TableOfContentsTextNode {
  type: 'text';
  id: string;
  href: string;
  value: string;
}

function isHtmlElementNode(node: Node): node is HtmlElementNode {
  return (
    typeof node === 'object' &&
    node.type === 'element' &&
    'tagName' in node &&
    typeof node.tagName === 'string' &&
    'properties' in node &&
    typeof node.properties === 'object'
  );
}

function isHeadingNode(node: Node): node is HeadingNode {
  return (
    isHtmlElementNode(node) && HEADINGS.includes(node.tagName as HeadingTagName)
  );
}

function findHeadings(node: Node): HeadingNode[] {
  const headingNodes: HeadingNode[] = [];
  findHeadingsRecursive(node, headingNodes);
  return headingNodes;
}

function findHeadingsRecursive(node: Node, headingNodes: HeadingNode[]): void {
  if (isHeadingNode(node)) {
    headingNodes.push(node);
  }

  if ('children' in node && node.children) {
    const parent = node as Parent;
    for (const child of parent.children) {
      findHeadingsRecursive(child, headingNodes);
    }
  }
}

export function getInnerText(node: Node): string {
  let text = '';

  if (node.type === 'text' && 'value' in node) {
    text += node.value || '';
  }

  if ('children' in node && node.children) {
    const parent = node as Parent;
    for (const child of parent.children) {
      text += getInnerText(child);
    }
  }

  return text;
}

interface TocLevel {
  depth: number;
  headingNumber: number;
  list: TableOfContentsListNode;
}

function createTocList(headings: HeadingNode[]): TableOfContentsListNode {
  let levels: TocLevel[] = [];
  let currentLevel: TocLevel = {
    depth: 0,
    headingNumber: 0,
    list: undefined as unknown as TableOfContentsListNode,
  };

  for (const heading of headings) {
    const headingNumber = parseInt(heading.tagName.slice(-1), 10);

    if (headingNumber > currentLevel.headingNumber) {
      // This is a higher heading number, so start a new level.
      const depth = currentLevel.depth + 1;
      const level = {
        depth,
        headingNumber,
        list: createList(heading, depth),
      };

      // Add the new list to the previous level's list.
      if (currentLevel.list) {
        const lastItem = currentLevel.list.children.slice(-1)[0];
        lastItem.children.push(level.list);
      }

      levels.push(level);
      currentLevel = level;
    } else {
      if (headingNumber < currentLevel.headingNumber) {
        // This is a lower heading number, so we need to go up to a previous level.
        for (let i = levels.length - 2; i >= 0; i--) {
          const level = levels[i];
          if (level.headingNumber === headingNumber) {
            // We found the previous level that matches this heading.
            levels = levels.slice(0, i + 1);
            currentLevel = level;
            break;
          }
        }

        // If headings are in an incorrect order, then we may need to adjust the headingNumber.
        currentLevel.headingNumber = Math.min(
          currentLevel.headingNumber,
          headingNumber
        );
      }

      // This heading is the same level as the previous heading.
      const listItem = createListItem(heading);
      currentLevel.list.children.push(listItem);
    }
  }

  if (levels.length === 0) {
    return createList(undefined, 0);
  } else {
    return levels[0].list;
  }
}

function createList(
  heading: HeadingNode | undefined,
  depth: number
): TableOfContentsListNode {
  const list: TableOfContentsListNode = {
    type: 'list',
    depth,
    children: [],
  };

  if (heading) {
    const listItem = createListItem(heading);
    list.children.push(listItem);
  }

  return list;
}

function createListItem(heading: HeadingNode): TableOfContentsListItemNode {
  return {
    type: 'item',
    children: [
      {
        type: 'text',
        id: heading.properties.id ?? '',
        href: `#${heading.properties.id ?? ''}`,
        value: getInnerText(heading),
      },
    ],
  };
}

export type TableOfContents = TableOfContentsListNode;

export interface TableOfContentsOutput {
  result: TableOfContents;
}

export function rehypeTableOfContents(
  this: Processor,
  tableOfContents: Partial<TableOfContentsOutput>
): Transformer {
  return function transformer(root: Node): Node {
    const headings = findHeadings(root);
    tableOfContents.result = createTocList(headings);
    return root;
  };
}
